import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {createUseStyles} from 'react-jss';
import ExcludeFromLookup from '../../../enums/ExcludeFromLookup';
import FieldTypes from '../../../enums/FieldTypes';
import AutocompleteFieldWrapper from '../../../UI/AutocompleteField/AutocompleteFieldWrapper/AutocompleteFieldWrapper';
import {updateDeliveryContactNumber} from '../../actions/address-actions';
import Input from '../../form/InputField';
import SelectField from '../../form/SelectField';
import makeGetIsUsingAddressLookup from '../../selectors/DeliveryAddressSelectors/makeGetIsUsingAddressLookup';
import cityRequiredForDeliveryCountry
from '../../selectors/DeliveryOptionsSelectors/cityRequiredForDeliveryCountrySelector';
import stateRequiredForDeliveryCountry
from '../../selectors/DeliveryOptionsSelectors/stateRequiredForDeliveryCountrySelector';
import getAutocompleteStatus from '../../selectors/getAutocompleteStatus';
import makeGetShouldShowContactNumberAfterAddressee from '../../selectors/makeGetShouldShowContactNumberAfterAddressee';
import getTaxSubdivisionEnabled from '../../selectors/getTaxSubdivisionEnabled';
import {isDeliveryCountryTaxable} from '../../selectors/TaxSelectors';
import AddressLookup from './AddressLookup/AddressLookupArbiter';
import ContactNumber from './ContactNumber/ContactNumber';
import legacyNameUtil from './legacy-name-util';
import PostcodeAnywhere from './PostcodeAnywhere';
import Spacing from '../../style-helper/Spacing';

const useStyles = createUseStyles({
  afterLookupFields: {
    marginTop: Spacing.triple,
  },
});

export function AddressFields(props) {
  const classes = useStyles();

  const onBlur = (event) => {
    if (event.target.name === 'deliveryAddress.postCode'
      || (event.target.name === 'deliveryAddress.townCity' && props.cityRequiredForDelivery)
      || (event.target.name === 'deliveryAddress.county' && props.stateRequiredForDelivery)) {
      props.updateDeliveryOptions();
    }
  };
  
  const onChange = (event) => {
    if (event.target.name === 'deliveryAddress.county' && props.isDeliveryCountryTaxable) {
      props.onTaxCountyChange(event);
    }
  };
  
  const fieldToComponent = (definedField) => {
    const key = `${props.fieldNamePrefix}--${props.addressType}--${definedField.name}`;
    const legacyNames = legacyNameUtil.get(props.addressType, props.fieldNamePrefix, definedField.name);

    const commonProps = {
      key,
      definedField,
      disabled: props.disabled,
      fieldContainer: props.fieldContainer,
      fieldNamePrefix: props.fieldNamePrefix,
    };
    
    switch (definedField.element) {
      case 'select':
        return (
          <SelectField
            {...commonProps}
            legacyNames={legacyNames}
            value={props.fieldValues[definedField.name]}
            onChange={onChange}
            onBlur={onBlur}
          />
        );
      case 'PostcodeAnywhere':
        return (
          <PostcodeAnywhere
            {...commonProps}
            addressType={props.addressType}
            resetAddress={props.resetAddress}
            fieldValues={props.fieldValues}
            updateDeliveryOptions={props.updateDeliveryOptions}
          />
        );
      case FieldTypes.AUTOCOMPLETE:
        return (
          <AutocompleteFieldWrapper
            {...commonProps}
            addressType={props.addressType}
            value={props.fieldValues[definedField.name]}
            legacyNames={legacyNames}
            onBlur={onBlur}
          />
        );
      default:
        return (
          <>
            <Input
              {...commonProps}
              addressType={props.addressType}
              legacyNames={legacyNames}
              value={props.fieldValues[definedField.name]}
              onBlur={onBlur}
              autoCompleteState={props.autoCompleteState}
            />
            {props.shouldShowContactNumberAfterAddressee && definedField.name === 'addressee' && (
              <ContactNumber shouldShowContactNumberAfterAddressee={props.shouldShowContactNumberAfterAddressee} fieldContainer={props.fieldContainerPhone.fieldUpdateDispatcher(props.updateContactNumberDispatcher)} requiresHelpLabel />
            )}
          </>
        );
    }
  };
  
  const excludedFromLookupBefore = (definedField) => {
    if (definedField.name === 'companyName') {
      if (definedField.hideCondition) {
        return false;
      }
      return !!((props.usingAddressLookup && definedField.excludeFromAddressLookup === ExcludeFromLookup.BEFORE));
    }
    return definedField.excludeFromAddressLookup === ExcludeFromLookup.BEFORE;
  };
  
  const excludedFromLookupAfter = (definedField) => {
    if (definedField.hideCondition) {
      return false;
    }
    return definedField.excludeFromAddressLookup === ExcludeFromLookup.AFTER;
  };

  const includedInLookup = (definedField) => {
    if (definedField.hideCondition) {
      return false;
    }
    if (definedField.name === 'companyName') {
      return !props.usingAddressLookup;
    }

    return !definedField.excludeFromAddressLookup;
  };
  
  return (
    <>
      {props.definedFields.filter(excludedFromLookupBefore).map(fieldToComponent)}
      {(props.shouldRenderAddressLookUp) && (
        <AddressLookup
          disabled={props.disabled}
          className={classes.manualAddressFormMarginBottom}
          addressType={props.addressType}
          country={props.fieldValues.country}
          useNewDispatcherForBillingAddress={props.useNewDispatcherForBillingAddress}
        >
          {props.definedFields.filter(includedInLookup).map(fieldToComponent)}
        </AddressLookup>
      )}
      {props.definedFields.filter(excludedFromLookupAfter)?.length ? (
        <div className={classes.afterLookupFields}>
          {props.definedFields.filter(excludedFromLookupAfter).map(fieldToComponent)}
        </div>
      ) : null}
    </>
  );
}

const mapStateToProps = (state, ownProps) => {
  const getShouldShowContactNumberAfterAddressee = makeGetShouldShowContactNumberAfterAddressee(ownProps.addressType);
  
  return {
    usingAddressLookup: makeGetIsUsingAddressLookup(ownProps?.fieldValues?.country)(state),
    isDeliveryCountryTaxable: isDeliveryCountryTaxable(state),
    cityRequiredForDelivery: cityRequiredForDeliveryCountry(state),
    stateRequiredForDelivery: (stateRequiredForDeliveryCountry(state) || isDeliveryCountryTaxable(state)) || getTaxSubdivisionEnabled(state),
    autoCompleteState: getAutocompleteStatus(state),
    shouldShowContactNumberAfterAddressee: getShouldShowContactNumberAfterAddressee(state),
  };
};

AddressFields.defaultProps = {
  onTaxCountyChange() {
  },
  shouldRenderAddressLookUp: true,
};

AddressFields.propTypes = {
  addressType: PropTypes.oneOf(['delivery', 'billing', 'contact']).isRequired,
  definedFields: PropTypes.array.isRequired,
  fieldContainer: PropTypes.object.isRequired,
  fieldContainerPhone: PropTypes.object,
  fieldValues: PropTypes.object.isRequired,
  isDeliveryCountryTaxable: PropTypes.bool.isRequired,
  cityRequiredForDelivery: PropTypes.bool.isRequired,
  stateRequiredForDelivery: PropTypes.bool.isRequired,
  shouldShowContactNumberAfterAddressee: PropTypes.bool.isRequired,
  updateContactNumberDispatcher: PropTypes.func.isRequired,

  // Optional
  fieldNamePrefix: PropTypes.string,
  autoCompleteState: PropTypes.string,
  resetAddress: PropTypes.func,
  disabled: PropTypes.bool,
  usingAddressLookup: PropTypes.bool,
  updateDeliveryOptions: PropTypes.func,
  onTaxCountyChange: PropTypes.func,
  useNewDispatcherForBillingAddress: PropTypes.bool,
  shouldRenderAddressLookUp: PropTypes.bool,
};

const mapDispatchToProps = dispatch => ({
  updateContactNumberDispatcher: (_, value) => {
    dispatch(updateDeliveryContactNumber(value));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(AddressFields);
